import React, { useState, useEffect, useContext } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { AuthContext, AuthProvider } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import TrafficLightDetail from './components/TrafficLightDetail';
import Footer from './components/Footer';
import ConfirmModal from './components/ConfirmModal';
import Login from './components/Login';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

const AppContent = () => {
  const [trafficLights, setTrafficLights] = useState([]);
  const [editingLight, setEditingLight] = useState(null);
  const [controllingLight, setControllingLight] = useState(null);
  const [controllingLights, setControllingLights] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const { pathname } = window.location;
    switch (pathname) {
      case '/home':
        return 'home';
      case '/traffic-lights':
        return 'traffic-lights';
      case '/map':
        return 'map';
      case '/settings':
        return 'settings';
      default:
        return 'home';
    }
  });
  const [viewMode, setViewMode] = useState('grid');
  const [useSSE, setUseSSE] = useState(true);
  const [serverTime, setServerTime] = useState(new Date('2025-05-30T11:22:00+07:00'));
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteKey, setConfirmDeleteKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const API_URL = 'https://trafficlight.saigoniot.vn/api/dpt';
  const CONTROL_URL = 'https://trafficlight.saigoniot.vn/control/dpt';
  const UPDATE_URL = 'https://trafficlight.saigoniot.vn/control/dpt/control';

  const { isAuthenticated, logout, token, fullname } = useContext(AuthContext);
  const location = useLocation();
  const [userName, setUserName] = useState(fullname || 'Users');

  useEffect(() => {
    switch (location.pathname) {
      case '/home':
        setActiveTab('home');
        break;
      case '/traffic-lights':
        setActiveTab('traffic-lights');
        break;
      case '/map':
        setActiveTab('map');
        break;
      case '/settings':
        setActiveTab('settings');
        break;
      default:
        if (!location.pathname.startsWith('/traffic-light-detail')) {
          setActiveTab('home');
        }
        break;
    }
  }, [location.pathname]);

  const fetchTrafficLights = async () => {
    try {
      console.log('Fetching traffic lights, token:', token);
      const response = await fetch(API_URL, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonErr) {
        const lines = text.split('\n').filter(line => line.trim() !== '');
        let jsonData = '';
        for (let i = lines.length - 1; i >= 0; i--) {
          if (lines[i].startsWith('data:')) {
            jsonData = lines[i].replace('data: ', '');
            break;
          }
        }
        if (jsonData) {
          data = JSON.parse(jsonData);
        } else {
          throw new Error('Không tìm thấy dữ liệu hợp lệ (fetch)');
        }
      }

      if (Array.isArray(data)) {
        setTrafficLights(data);
        setError('');
      } else {
        setError('Dữ liệu nhận được không phải là mảng');
      }
    } catch (err) {
      setError(`Lỗi khi lấy dữ liệu qua fetch: ${err.message}`);
      console.error('Lỗi fetch:', err);
    }
  };

  useEffect(() => {
    let source;
    console.log('useEffect running, isAuthenticated:', isAuthenticated, 'token:', token);

    if (!isAuthenticated) {
      console.log('Not authenticated, skipping fetch/SSE');
      return;
    }

    const startSSE = () => {
      console.log('Starting SSE, token:', token);
      source = new EventSource(API_URL, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      source.onmessage = (event) => {
        try {
          if (!event.data || event.data.trim() === '') {
            return;
          }
          const data = JSON.parse(event.data);
          if (Array.isArray(data)) {
            setTrafficLights(prev => {
              const newTrafficLights = data;
              if (JSON.stringify(prev) !== JSON.stringify(newTrafficLights)) {
                return newTrafficLights;
              }
              return prev;
            });
            setError('');
          } else {
            setError('Dữ liệu SSE không phải là mảng');
          }
        } catch (err) {
          setError(`Lỗi khi xử lý dữ liệu SSE: ${err.message}`);
          console.error('Lỗi SSE:', err);
        }
      };
      source.onerror = () => {
        setError('Kết nối dữ liệu bị gián đoạn. Đang thử lại...');
        source.close();
        setUseSSE(true);
      };
    };

    if (useSSE) {
      startSSE();
    } else {
      fetchTrafficLights();
      const interval = setInterval(fetchTrafficLights, 1000);
      return () => clearInterval(interval);
    }

    return () => {
      if (source) {
        console.log('Closing SSE connection...');
        source.close();
      }
    };
  }, [isAuthenticated, useSSE]);

  useEffect(() => {
    const interval = setInterval(() => {
      setServerTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const updateTrafficLight = async (id, updatedLight, onComplete) => {
    try {
      if (!id || !updatedLight) {
        throw new Error('Dữ liệu không hợp lệ để cập nhật đèn giao thông');
      }

      const response = await fetch(CONTROL_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id, ...updatedLight }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Không thể cập nhật đèn giao thông: ${errorText}`);
      }

      setEditingLight(null);
      fetchTrafficLights();
      setError('');
      onComplete(true);
    } catch (err) {
      setError(`Lỗi khi cập nhật đèn giao thông: ${err.message}`);
      console.error('Lỗi khi cập nhật:', err);
      if (onComplete) onComplete(false);
    }
  };

  const deleteTrafficLight = async (id) => {
    setConfirmDelete(id);
  };

  const confirmDeleteAction = async () => {
    setIsLoading(true);
    try {
      const id = confirmDelete;
      const response = await fetch(CONTROL_URL, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id }),
      });
      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : {};
      if (!response.ok) {
        throw new Error(`Không thể xóa đèn: ${data.error || data.message || 'Không có chi tiết lỗi'}`);
      }
      setError('');
      fetchTrafficLights();
      toast.warning('Xóa đèn thành công!', {
        position: 'top-center',
        autoClose: 3000,
      });
    } catch (err) {
      setError(`Lỗi khi xóa đèn: ${err.message}`);
      console.error('Delete error:', err);
      toast.error(`Xóa không thành công: ${err.message}`, {
        position: 'top-center',
        autoClose: 3000,
      });
    } finally {
      setConfirmDelete(null);
      setConfirmDeleteKey(prev => prev + 1);
      setIsLoading(false);
    }
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  const controlTrafficLight = async (controlData, onComplete) => {
    try {
      if (Array.isArray(controlData)) {
        const results = await Promise.all(
          controlData.map(async (data) => {
            const response = await fetch(UPDATE_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error(`Không thể điều khiển đèn ${data.DeviceId}`);
            return true;
          })
        );
        if (results.every((result) => result)) {
          setControllingLights(null);
          setError('');
          fetchTrafficLights();
          if (onComplete) onComplete(true);
        } else {
          throw new Error('Một số đèn không thể điều khiển');
        }
      } else {
        const response = await fetch(UPDATE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(controlData),
        });
        if (!response.ok) throw new Error('Không thể điều khiển đèn giao thông');
        setControllingLight(null);
        setError('');
        fetchTrafficLights();
        if (onComplete) onComplete(true);
      }
    } catch (err) {
      setError(`Lỗi khi điều khiển đèn giao thông: ${err.message}`);
      console.error('Control error:', err);
      if (onComplete) onComplete(false);
    }
  };

  const handleControlSingle = (light) => {
    setControllingLight(light);
  };

  const handleControlBatch = (lights) => {
    const lightsArray = Array.isArray(lights) ? lights : [lights];
    setControllingLights(lightsArray);
  };

  const handleLogout = () => {
    logout();
    setUserName('Users'); // Reset về 'Users' khi logout
  };

  const handleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullScreen(!isFullScreen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen((prev) => !prev);
    if (!isUserMenuOpen && fullname) {
      setUserName(fullname); // Cập nhật userName thành fullname khi mở menu
    }
  };

  const handleUserInfo = () => {
    setIsUserMenuOpen(false);
  };

  const handleChangePassword = () => {
    setIsUserMenuOpen(false);
  };

  const handleMouseEnter = () => {
    setIsUserMenuOpen(true); // Mở menu khi chuột vào khu vực
    if (fullname) {
      setUserName(fullname);
    }
  };

  const handleMouseLeave = () => {
    setIsUserMenuOpen(false); // Đóng menu khi chuột rời khỏi toàn bộ khu vực
  };

  const totalLights = trafficLights.length;
  const errorLights = trafficLights.filter(light => light.current_status === 'unknown').length;

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="flex flex-row h-screen">
      <ToastContainer />
      <div className="flex flex-col w-full">
        <div className="flex flex-1">
          <Sidebar
            isOpen={isSidebarOpen}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onToggle={() => setIsSidebarOpen((prev) => !prev)}
          />
          <div
            className="flex-1 min-h-screen"
            style={{
              marginLeft: isSidebarOpen && window.innerWidth >= 768 ? '200px' : '60px',
              transition: 'margin-left 0.3s ease',
            }}
          >
            <header className="bg-gray-500 text-white p-4 flex justify-between items-center sticky top-0 z-10">
              <div className="flex items-center titleBar">              
              <div className="text-xl font-bold flex items-left"
                  style={{
                    display: window.innerWidth <= 768 ? 'none' : 'block'
                  }}
              >                
                  <span className="titleBar">Đèn giao thông thông minh</span>
                </div>
              </div>
              <div className="flex items-center space-x-6 relative titleSearch">
                <input
                  type="text"
                  placeholder="Tìm kiếm đèn theo tên ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border rounded px-2 py-1 text-black searchText"
                />
                <button onClick={handleFullScreen} className="bg-blue-500 px-3 py-1 rounded hover:bg-blue-600 flex items-center">
                  {isFullScreen ? (
                    <i className="material-icons text-[20px]">fullscreen_exit</i>
                  ) : (
                    <i className="material-icons text-[20px]">fullscreen</i>
                  )}
                </button>
                <div
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    onClick={toggleUserMenu}
                    className="bg-gray-600 px-3 py-1 rounded hover:bg-gray-700 flex items-center"
                  >
                    <i className="material-icons text-[20px] mr-1">person</i>
                  </button>
                  {isUserMenuOpen && (
                    <div
                      className="absolute right-0 top-100% w-48 bg-white rounded shadow-lg z-50" // Tăng z-index và sửa top
                    >
                      <div className="px-4 py-2 text-gray-800 font-semibold flex items-center">
                        <i className="material-icons text-[18px] mr-2">person</i>
                        {userName}
                      </div>
                      <hr className="border-gray-200" />
                      <button
                        onClick={handleUserInfo}
                        className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 flex items-center"
                      >
                        <i className="material-icons text-[18px] mr-2">info</i>
                        Thông tin user
                      </button>
                      <button
                        onClick={handleChangePassword}
                        className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 flex items-center"
                      >
                        <i className="material-icons text-[18px] mr-2">lock</i>
                        Đổi mật khẩu
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 flex items-center"
                      >
                        <i className="material-icons text-[18px] mr-2">logout</i>
                        Đăng xuất
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </header>

            <div className="bg-gray-100 p-4 flex justify-between items-center border-b border-gray-300 sticky top-[60px]">
              <div className="flex-1 text-center">
                <span className="font-semibold">Tổng số đèn: </span>
                <span className="text-blue-600">{totalLights}</span>
              </div>
              <div className="flex-1 text-center">
                <span className="font-semibold">Số đèn lỗi: </span>
                <span className="text-red-500">{errorLights}</span>
              </div>
              <div className="flex-1 text-center">
                <span className="font-semibold">Thời gian server: </span>
                <span className="text-green-600">{serverTime.toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}</span>
              </div>
            </div>

            <div className="p-4">
              <Switch>
                <Route exact path="/home">
                  <MainContent
                    activeTab="home"
                    trafficLights={trafficLights}
                    editingLight={editingLight}
                    controllingLight={controllingLight}
                    controllingLights={controllingLights}
                    error={error}
                    searchTerm={searchTerm}
                    viewMode={viewMode}
                    useSSE={useSSE}
                    setViewMode={setViewMode}
                    onEdit={setEditingLight}
                    onControlSingle={handleControlSingle}
                    onControlBatch={handleControlBatch}
                    onDelete={deleteTrafficLight}
                    onUpdateTrafficLight={updateTrafficLight}
                    onControlTrafficLight={controlTrafficLight}
                    setError={setError}
                    setEditingLight={setEditingLight}
                    setControllingLight={setControllingLight}
                    setControllingLights={setControllingLights}
                  />
                </Route>
                <Route exact path="/traffic-lights">
                  <MainContent
                    activeTab="traffic-lights"
                    trafficLights={trafficLights}
                    editingLight={editingLight}
                    controllingLight={controllingLight}
                    controllingLights={controllingLights}
                    error={error}
                    searchTerm={searchTerm}
                    viewMode={viewMode}
                    useSSE={useSSE}
                    setViewMode={setViewMode}
                    onEdit={setEditingLight}
                    onControlSingle={handleControlSingle}
                    onControlBatch={handleControlBatch}
                    onDelete={deleteTrafficLight}
                    onUpdateTrafficLight={updateTrafficLight}
                    onControlTrafficLight={controlTrafficLight}
                    setError={setError}
                    setEditingLight={setEditingLight}
                    setControllingLight={setControllingLight}
                    setControllingLights={setControllingLights}
                  />
                </Route>
                <Route path="/map">
                  <MainContent
                    activeTab="map"
                    trafficLights={trafficLights}
                    editingLight={editingLight}
                    controllingLight={controllingLight}
                    controllingLights={controllingLights}
                    error={error}
                    searchTerm={searchTerm}
                    viewMode={viewMode}
                    useSSE={useSSE}
                    setViewMode={setViewMode}
                    onEdit={setEditingLight}
                    onControlSingle={handleControlSingle}
                    onControlBatch={handleControlBatch}
                    onDelete={deleteTrafficLight}
                    onUpdateTrafficLight={updateTrafficLight}
                    onControlTrafficLight={controlTrafficLight}
                    setError={setError}
                    setEditingLight={setEditingLight}
                    setControllingLight={setControllingLight}
                    setControllingLights={setControllingLights}
                  />
                </Route>
                <Route path="/settings">
                  <MainContent
                    activeTab="settings"
                    trafficLights={trafficLights}
                    editingLight={editingLight}
                    controllingLight={controllingLight}
                    controllingLights={controllingLights}
                    error={error}
                    searchTerm={searchTerm}
                    viewMode={viewMode}
                    useSSE={useSSE}
                    setViewMode={setViewMode}
                    onEdit={setEditingLight}
                    onControlSingle={handleControlSingle}
                    onControlBatch={handleControlBatch}
                    onDelete={deleteTrafficLight}
                    onUpdateTrafficLight={updateTrafficLight}
                    onControlTrafficLight={controlTrafficLight}
                    setError={setError}
                    setEditingLight={setEditingLight}
                    setControllingLight={setControllingLight}
                    setControllingLights={setControllingLights}
                  />
                </Route>
                <Route path="/traffic-light-detail/:id">
                  <TrafficLightDetail />
                </Route>
                <Route exact path="/">
                  <Redirect to="/home" />
                </Route>
                <Route path="/login">
                  <Login />
                </Route>
              </Switch>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      {confirmDelete && (
        <ConfirmModal
          key={confirmDeleteKey}
          message="Bạn có chắc muốn xóa đèn này?"
          onConfirm={confirmDeleteAction}
          onCancel={cancelDelete}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

const App = () => (
  <AuthProvider>
    <Router>
      <AppContent />
    </Router>
  </AuthProvider>
);

export default App;